import React from 'react';

const BOOT_SKELETON_STYLE: React.CSSProperties = {
  paddingTop: 32
};
const SKELETON_ROW_STYLE: React.CSSProperties = {
  paddingLeft: 56
};
const WIDTH_NARROW_STYLE: React.CSSProperties = {
  width: '66.666667%'
};
const WIDTH_MEDIUM_STYLE: React.CSSProperties = {
  width: '80%'
};
const WIDTH_WIDE_STYLE: React.CSSProperties = {
  width: '83.333333%'
};
const WIDTH_LABEL_LONG_STYLE: React.CSSProperties = {
  width: 128
};
const WIDTH_STYLES = {
  narrow: WIDTH_NARROW_STYLE,
  medium: WIDTH_MEDIUM_STYLE,
  wide: WIDTH_WIDE_STYLE
} as const;

type FeedSkeletonWidth = keyof typeof WIDTH_STYLES;

/** Displays the initial feed loading skeleton over an empty engine window. */
export function BootSkeleton() {
  return (
    <div className="pointer-events-none absolute inset-0 px-4 opacity-50" aria-hidden="true" data-feed-boot-skeleton style={BOOT_SKELETON_STYLE}>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <FeedSkeletonRow />
        <FeedSkeletonRow width="narrow" />
        <FeedSkeletonRow width="wide" />
      </div>
    </div>
  );
}

/** Renders the placeholder row used while a feed item is still being materialized. */
export function FeedPlaceholderRow() {
  return (
    <div className="h-full px-4 opacity-50">
      <div className="mx-auto size-full max-w-2xl">
        <FeedSkeletonRow compact />
      </div>
    </div>
  );
}

/** Shared two-line message skeleton used by feed loading surfaces. */
export function FeedSkeletonRow({ compact = false, width = 'medium' }: { readonly compact?: boolean; readonly width?: FeedSkeletonWidth }) {
  return (
    <div className={compact ? 'flex h-full flex-col justify-center gap-1' : 'flex flex-col gap-2'} style={SKELETON_ROW_STYLE}>
      <div
        className={`${compact ? 'h-2' : 'h-3'} shrink-0 animate-pulse rounded-full bg-gray-200 dark:bg-neutral-800`}
        style={WIDTH_LABEL_LONG_STYLE}
      />
      <div
        className={`${compact ? 'h-6' : 'h-9'} shrink-0 animate-pulse rounded-2xl bg-gray-100 dark:bg-neutral-900`}
        style={WIDTH_STYLES[width]}
      />
    </div>
  );
}
